import React from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils/cn';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'ghost' | 'highlight' | 'elevated';
  hover?: boolean;
}

export function Card({ className, variant = 'default', hover, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-4 border transition-all duration-3 ease',
        variant === 'default' && 'border-border bg-surface shadow-sm',
        variant === 'ghost' && 'border-transparent bg-transparent',
        variant === 'highlight' && 'border-primary/10 bg-primary/[0.02]',
        variant === 'elevated' && 'border-border bg-surface shadow-elevation-md',
        hover && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md',
        className
      )}
      {...props}
    />
  );
}
Card.displayName = "Card";

interface CardMediaProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  aspect?: 'video' | 'square' | 'wide' | 'portrait';
}

export function CardMedia({ className, src, alt, aspect = 'video', ...props }: CardMediaProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-t-4',
        aspect === 'video' && 'aspect-[16/10]',
        aspect === 'square' && 'aspect-square',
        aspect === 'wide' && 'aspect-[2/1]',
        aspect === 'portrait' && 'aspect-[3/4]',
        className
      )}
      {...props}
    >
      {src ? (
        <Image src={src} alt={alt ?? ''} fill className="object-cover" unoptimized />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-surface-tertiary text-foreground/10" />
      )}
    </div>
  );
}
CardMedia.displayName = "CardMedia";

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

export function CardHeader({ className, ...props }: CardHeaderProps) {
  return <div className={cn('flex flex-col gap-1 p-6 pb-3', className)} {...props} />;
}
CardHeader.displayName = "CardHeader";

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h2' | 'h3' | 'h4';
}

export function CardTitle({ className, as: Tag = 'h3', ...props }: CardTitleProps) {
  return (
    <Tag className={cn('font-heading text-4 font-semibold tracking-tight text-fg', className)} {...props} />
  );
}
CardTitle.displayName = "CardTitle";

interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export function CardDescription({ className, ...props }: CardDescriptionProps) {
  return <p className={cn('text-3 text-fg-secondary', className)} {...props} />;
}
CardDescription.displayName = "CardDescription";

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {}

export function CardContent({ className, ...props }: CardContentProps) {
  return <div className={cn('p-6 pt-0', className)} {...props} />;
}
CardContent.displayName = "CardContent";

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export function CardFooter({ className, ...props }: CardFooterProps) {
  return <div className={cn('flex items-center p-6 pt-3', className)} {...props} />;
}
CardFooter.displayName = "CardFooter";
